import { Helmet } from 'react-helmet-async';
import Calculator from '../components/Calculator';
import RelatedCalculators from '../components/RelatedCalculators';
import { detectLocaleSync } from '../locales/config';

const COLORS = { bg: "#0a0a0f", text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0", border: "#1e1e2e" };

const faqItems = [
  {
    q: 'What is the debt avalanche method?',
    a: 'Pay minimums on all debts, then put all extra money toward the highest interest rate debt first. Once it\'s eliminated, move to the next highest. This is the mathematically optimal strategy.',
  },
  {
    q: 'Avalanche vs Snowball — which saves more money?',
    a: 'Avalanche always saves more money in interest. The difference can be hundreds or thousands of dollars depending on your balances and rates. Use the comparison above to see the exact difference for your debts.',
  },
  {
    q: 'How much can I save with the avalanche method?',
    a: 'It depends on your debt mix, but people with high-interest credit cards often save $1,000–$5,000+ in interest compared to the snowball method. The calculator shows your exact savings.',
  },
  {
    q: "What's the biggest risk with the avalanche method?",
    a: "If your highest-interest debt is also your largest, it might take a long time before you eliminate your first debt. Some people lose motivation. If that's you, start with snowball and switch to avalanche later.",
  },
];

export default function AvalancheCalculator() {
  const locale = detectLocaleSync();
  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", color: COLORS.text }}>
      <Helmet>
        <title>Free Debt Avalanche Calculator — ClearMyDebt</title>
        <meta name="description" content="Pay off high-interest debt first and save the most money. Free debt avalanche calculator — find your optimal payoff order instantly." />
        <link rel="canonical" href="https://clearmydebt.app/debt-avalanche-calculator/" />
        <meta property="og:title" content="Free Debt Avalanche Calculator — ClearMyDebt" />
        <meta property="og:description" content="Pay highest-interest debt first and save the most money. Free, instant, no signup." />
        <meta property="og:url" content="https://clearmydebt.app/debt-avalanche-calculator/" />
        <meta property="og:image" content="https://clearmydebt.app/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://clearmydebt.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        })}</script>
      </Helmet>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '48px 24px 0' }}>
        <div style={{ fontSize: '11px', letterSpacing: '3px', color: COLORS.accent, fontWeight: 700, marginBottom: '12px' }}>
          <a href="/" style={{ color: COLORS.accent, textDecoration: 'none' }}>ClearMyDebt</a>
        </div>
        <h1 style={{ fontSize: 'clamp(24px,5vw,36px)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 12px', background: `linear-gradient(135deg,#fff,${COLORS.accent})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Free Debt Avalanche Calculator
        </h1>
        <p style={{ color: COLORS.muted, fontSize: '1.05rem', marginBottom: 0, lineHeight: 1.6 }}>
          Target your highest interest rate debt first and save the maximum amount in interest.
          The mathematically optimal payoff strategy — see your savings instantly.
        </p>
      </div>

      <Calculator
        defaultMethod="avalanche"
        locale={locale}
        showHelmet={false}
        showHeader={false}
      />

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 24px 64px' }}>
        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 12px' }}>
          How the Debt Avalanche method works
        </h2>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 28 }}>
          List all your debts and sort by interest rate, highest first. Pay minimums on
          everything, then put all extra money toward the highest-rate debt. When it's paid
          off, roll that payment into the next highest rate. The "avalanche" of freed-up
          payments accelerates each subsequent payoff.
        </p>

        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 12px' }}>
          Why Avalanche is the optimal strategy
        </h2>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 28 }}>
          Interest compounds against you daily. By targeting the highest rate first, you
          reduce the fastest-growing debt immediately. Every dollar of interest you avoid
          is a dollar that stays in your pocket. For people with credit card debt at 20%+
          APR, avalanche can save thousands over snowball.
        </p>

        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 20px' }}>
          Frequently Asked Questions
        </h2>
        {faqItems.map(({ q, a }) => (
          <div key={q} style={{ marginBottom: 20, padding: '16px', background: '#13131a', borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
            <h3 style={{ color: COLORS.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 8px' }}>{q}</h3>
            <p style={{ color: COLORS.muted, lineHeight: 1.6, margin: 0 }}>{a}</p>
          </div>
        ))}

      </div>

      <RelatedCalculators locale={locale} exclude="/debt-avalanche-calculator/" />
    </div>
  );
}
